import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  BookUser,
  Check,
  ChevronDown,
  Coins,
  Send as SendIcon,
  ShieldAlert,
  Snowflake,
} from "lucide-react";
import { gauntlet, ApiError, type BuiltTx } from "../lib/api";
import { formatAmount, formatFiat, shortenAddress, cx } from "../lib/format";
import { Badge, Button, Callout, Input, Panel } from "../components/ui";

type Stage = "compose" | "review" | "sent";

/**
 * The send flow, split into compose → review → broadcast.
 *
 * The review step is not decoration. It shows the exact fee, the change, and
 * any warnings the builder produced *before* the vault is used to sign, so the
 * user is authorising a specific transaction rather than an intention.
 */
export function SendPage() {
  const { accountId } = useParams();
  const id = Number(accountId);

  const [stage, setStage] = useState<Stage>("compose");
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [feeLevel, setFeeLevel] = useState<
    "fastest" | "fast" | "medium" | "slow"
  >("medium");
  const [built, setBuilt] = useState<BuiltTx | null>(null);
  const [txid, setTxid] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [rbf, setRbf] = useState(true);
  const [showCoins, setShowCoins] = useState(false);
  const [selectedUtxos, setSelectedUtxos] = useState<number[]>([]);
  const [showContacts, setShowContacts] = useState(false);
  const [saveContact, setSaveContact] = useState("");
  const [savedContact, setSavedContact] = useState(false);

  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: () => gauntlet.wallets(),
  });
  const account = wallets?.wallets
    .flatMap((w) => w.accounts)
    .find((a) => a.id === id);
  const wallet = wallets?.wallets.find((w) =>
    w.accounts.some((a) => a.id === id),
  );
  const chainId = account?.chain_id ?? "";

  const { data: chains } = useQuery({
    queryKey: ["chains"],
    queryFn: () => gauntlet.chains(),
  });
  const chain = chains?.chains.find((c) => c.id === chainId);

  const { data: fees } = useQuery({
    queryKey: ["fees", chainId],
    queryFn: () => gauntlet.estimateFee(chainId),
    enabled: Boolean(chainId),
    staleTime: 60_000,
  });

  /* Fiat at a glance. Not everyone reads coin amounts — the portfolio already
     resolves the per-user currency and each asset's price, so reuse it. */
  const { data: portfolio } = useQuery({
    queryKey: ["portfolio"],
    queryFn: () => gauntlet.portfolio(),
    staleTime: 60_000,
  });
  const currency = portfolio?.currency ?? "usd";
  const unitPrice =
    (chain?.coingeckoId ?? "") === ""
      ? null
      : (portfolio?.holdings.find(
          (h) => h.chainId === chainId && !h.token,
        )?.price ?? null);

  /** Base units → fiat cents, BigInt throughout (same as the server). */
  const fiatCentsFor = (base: string, decimals: number): string | null => {
    if (!unitPrice) return null;
    const PRECISION = 12;
    const [whole = "0", frac = ""] = unitPrice.split(".");
    const scaledPrice = BigInt(
      whole + frac.slice(0, PRECISION).padEnd(PRECISION, "0"),
    );
    const scaled = BigInt(base) * scaledPrice;
    const divisor = 10n ** BigInt(decimals + PRECISION - 2);
    return (scaled / divisor).toString(10);
  };
  const fiatText = (base: string, decimals: number): string | null => {
    const cents = fiatCentsFor(base, decimals);
    return cents !== null ? formatFiat(cents, currency) : null;
  };

  // Validate the destination as it is typed, so a wrong-chain paste is caught
  // immediately rather than at the point of signing.
  const { data: validation } = useQuery({
    queryKey: ["validate", chainId, to],
    queryFn: () => gauntlet.validateAddress(to, chainId),
    // Any non-trivial input must be checked. The old `> 10` floor meant a
    // short, obviously-wrong address was never validated at all, so the
    // button enabled and the failure surfaced only at build time.
    enabled: to.trim().length > 0 && Boolean(chainId),
  });

  /* Coin control. Only meaningful on UTXO chains — an account model has no
     coins to pick from. */
  const isUtxo = chain?.family === "utxo";
  const { data: utxoData, refetch: refetchUtxos } = useQuery({
    queryKey: ["utxos", id],
    queryFn: () => gauntlet.utxos(id),
    enabled: isUtxo,
  });

  const { data: contactData } = useQuery({
    queryKey: ["contacts"],
    queryFn: () => gauntlet.contacts(),
  });
  const contacts = (contactData?.contacts ?? []) as unknown as {
    id: number;
    name: string;
    address: string;
    chain_id: string | null;
  }[];

  /* Address poisoning. An attacker sends dust from an address that shares your
     real recipient's first and last characters, hoping you copy it out of your
     own history next time. The encoding is perfectly valid, so `validateAddress`
     cannot catch this — only a comparison against addresses you have used
     before can. */
  const { data: risk } = useQuery({
    queryKey: ["addr-risk", chainId, to],
    queryFn: () => gauntlet.checkAddress(chainId, to),
    enabled: to.trim().length > 10 && Boolean(chainId),
  });

  const addContact = useMutation({
    mutationFn: () =>
      gauntlet.addContact({ name: saveContact, address: to, chainId }),
    onSuccess: () => setSavedContact(true),
  });

  const freezeUtxo = useMutation({
    mutationFn: ({ utxoId, frozen }: { utxoId: number; frozen: boolean }) =>
      gauntlet.freezeUtxo(utxoId, frozen),
    onSuccess: () => refetchUtxos(),
  });

  const build = useMutation({
    mutationFn: () =>
      gauntlet.buildTx({
        accountId: id,
        outputs: [
          { address: to, amount: toBaseUnits(amount, chain?.decimals ?? 8) },
        ],
        ...(fees ? { feeRate: fees[feeLevel] } : {}),
        ...(selectedUtxos.length > 0 ? { selectedUtxoIds: selectedUtxos } : {}),
        rbf,
      }),
    onSuccess: (result) => {
      setBuilt(result);
      setStage("review");
      setError(null);
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not build the transaction.",
      ),
  });

  const send = useMutation({
    mutationFn: async () => {
      if (!built) throw new Error("Nothing built");
      // Every UTXO input is owned by a *leaf* key (…/0/0, …/0/1), not the
      // account key. The build response already carries each input's exact
      // derivation path — signing everything with the account path was a
      // guaranteed signature mismatch and an opaque 500 at broadcast time.
      const inputPaths = built.inputs?.map((i) => i.path);
      const signed = await gauntlet.signTx({
        accountId: id,
        payload: built.payload,
        format: built.format === "psbt" ? "psbt" : "rlp",
        ...(built.format === "psbt" &&
        inputPaths &&
        inputPaths.length > 0 &&
        inputPaths.every((p) => Boolean(p))
          ? { paths: inputPaths as string[] }
          : {}),
      });
      return gauntlet.broadcastTx(chainId, signed.signed.hex);
    },
    onSuccess: (result) => {
      setTxid(result.txid);
      setStage("sent");
      // Feeds the poisoning check above. Without this the known-address table
      // never fills up and every address looks equally unfamiliar.
      void gauntlet
        .rememberAddress({ chainId, address: to })
        .catch(() => undefined);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Broadcast failed."),
  });

  if (!account || !chain) {
    return (
      <Panel>
        <p className="py-8 text-center text-sm text-ink-faint">
          Account not found.
        </p>
      </Panel>
    );
  }

  const addressBad = validation && validation.valid === false;
  const sendable = chain.tier === "full";

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <Link
        to={`/wallets/${wallet?.id}`}
        className="inline-flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink"
      >
        <ArrowLeft size={15} />
        Back
      </Link>

      {!sendable && (
        <Callout
          tone="warn"
          title={`Sending is not available on ${chain.name}`}
        >
          Gauntlet can derive addresses
          {chain.tier === "watch" ? " and read balances" : ""} for this chain,
          but transaction building is not implemented for it yet. Receiving is
          safe.
        </Callout>
      )}

      {stage === "compose" && (
        <Panel title={`Send ${chain.symbol}`}>
          <div className="space-y-4">
            <div className="relative">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[13px] font-medium text-ink-dim">To address</span>
                {contacts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowContacts((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs text-ink-dim hover:text-brass"
                  >
                    <BookUser size={12} />
                    Contacts
                  </button>
                )}
              </div>
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value.trim())}
                placeholder={`${chain.name} address`}
                mono
                autoFocus
                error={
                  addressBad ? (validation?.reason ?? "Invalid address") : null
                }
              />

              {/* Re-pasting a raw address every time is exactly the habit that
                  address-poisoning attacks exploit. */}
              {showContacts && (
                <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-hairline bg-raised shadow-xl">
                  {contacts
                    .filter((c) => !c.chain_id || c.chain_id === chainId)
                    .map((contact) => (
                      <li key={contact.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setTo(contact.address);
                            setShowContacts(false);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-brass-wash"
                        >
                          <span className="text-sm font-medium">{contact.name}</span>
                          <span className="addr ml-auto text-xs text-ink-faint">
                            {shortenAddress(contact.address)}
                          </span>
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            {/* Poisoning risk. "lookalike" is the dangerous one: the address is
                perfectly valid, it just is not the one you meant. */}
            {risk && risk.risk !== "none" && (
              <Callout
                tone={
                  risk.risk === "lookalike"
                    ? "danger"
                    : risk.risk === "known_good"
                      ? "success"
                      : "warn"
                }
                title={
                  risk.risk === "lookalike"
                    ? "This address looks like one you have used — but is not it"
                    : risk.risk === "known_good"
                      ? "You have paid this address before"
                      : "First time sending here"
                }
              >
                <span className="inline-flex items-start gap-1.5">
                  {risk.risk === "lookalike" && (
                    <ShieldAlert size={13} className="mt-0.5 shrink-0" />
                  )}
                  <span>
                    {risk.message}
                    {risk.resembles && (
                      <>
                        {" "}
                        It resembles <span className="addr">{shortenAddress(risk.resembles)}</span>.
                        Check every character, not just the ends.
                      </>
                    )}
                    {risk.timesUsed ? ` Used ${risk.timesUsed} times.` : ""}
                  </span>
                </span>
              </Callout>
            )}

            {validation?.valid && (validation.alsoValidOn?.length ?? 0) > 0 && (
              <Callout
                tone="warn"
                title="This address is valid on more than one chain"
              >
                It would also be accepted by:{" "}
                {validation.alsoValidOn?.slice(0, 4).join(", ")}. Make sure you
                are sending on <strong>{chain.name}</strong> — funds sent to the
                wrong network are usually unrecoverable.
              </Callout>
            )}

            <Input
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
              placeholder="0.00"
              inputMode="decimal"
              mono
              suffix={chain.symbol}
            />

            {amount && Number(amount) > 0 && chain?.coingeckoId ? (
              <p className="num -mt-2 text-xs text-ink-faint">
                ≈ {fiatText(toBaseUnits(amount, chain.decimals), chain.decimals)}
              </p>
            ) : amount && Number(amount) > 0 && !chain?.coingeckoId ? (
              <p className="-mt-2 text-xs text-ink-faint">
                {chain.name} has no market price — test coins are worth nothing,
                so there is no fiat amount to show.
              </p>
            ) : null}

            <div className="space-y-1.5">
              <span className="block text-[13px] font-medium text-ink-dim">
                Fee
              </span>
              <div className="grid grid-cols-4 gap-1.5">
                {(["slow", "medium", "fast", "fastest"] as const).map(
                  (level) => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setFeeLevel(level)}
                      className={cx(
                        "rounded-lg border px-2 py-2 text-xs capitalize transition-colors",
                        feeLevel === level
                          ? "border-brass/50 bg-brass-wash text-brass"
                          : "border-hairline text-ink-dim hover:border-hairline-bright",
                      )}
                    >
                      <span className="block font-medium">{level}</span>
                      {fees && (
                        <span className="num mt-0.5 block text-[10px] text-ink-faint">
                          {fees.unit === "sat/vB"
                            ? `${fees[level]} sat/vB`
                            : "—"}
                        </span>
                      )}
                    </button>
                  ),
                )}
              </div>
            </div>

            {/* Replace-by-fee. Defaulting this on silently was a privacy and
                UX decision made on the user's behalf without telling them. */}
            {isUtxo && (
              <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-hairline p-3">
                <input
                  type="checkbox"
                  checked={rbf}
                  onChange={(e) => setRbf(e.target.checked)}
                  className="mt-0.5 accent-[var(--color-brass)]"
                />
                <span className="text-[13px]">
                  <span className="font-medium">Allow fee bumping (RBF)</span>
                  <span className="mt-0.5 block text-ink-faint">
                    Lets you re-send at a higher fee if it gets stuck. Some
                    merchants refuse to see RBF transactions as paid until they
                    confirm.
                  </span>
                </span>
              </label>
            )}

            {/* Coin control. A wallet that picks coins for you can undo careful
                separation of funds in a single spend. */}
            {isUtxo && (utxoData?.utxos.length ?? 0) > 0 && (
              <div className="rounded-lg border border-hairline">
                <button
                  type="button"
                  onClick={() => setShowCoins((v) => !v)}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
                >
                  <Coins size={14} className="text-brass" />
                  <span className="text-[13px] font-medium">Choose coins</span>
                  <span className="text-xs text-ink-faint">
                    {selectedUtxos.length > 0
                      ? `${selectedUtxos.length} selected`
                      : `${utxoData?.utxos.length} available · automatic`}
                  </span>
                  <ChevronDown
                    size={14}
                    className={cx(
                      "ml-auto text-ink-faint transition-transform",
                      showCoins && "rotate-180",
                    )}
                  />
                </button>

                {showCoins && (
                  <ul className="max-h-64 divide-y divide-hairline overflow-y-auto border-t border-hairline">
                    {(utxoData?.utxos ?? []).map((utxo) => {
                      const frozen = Number(utxo.frozen) === 1;
                      const picked = selectedUtxos.includes(utxo.id);
                      return (
                        <li key={utxo.id} className="flex items-center gap-2 px-3 py-2">
                          <input
                            type="checkbox"
                            checked={picked}
                            disabled={frozen}
                            onChange={(e) =>
                              setSelectedUtxos((prev) =>
                                e.target.checked
                                  ? [...prev, utxo.id]
                                  : prev.filter((x) => x !== utxo.id),
                              )
                            }
                            className="accent-[var(--color-brass)] disabled:opacity-40"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="num text-[13px]">
                              {formatAmount(utxo.value, chain.decimals ?? 8)} {chain.symbol}
                            </p>
                            <p className="addr truncate text-[10px] text-ink-faint">
                              {shortenAddress(utxo.txid, 8, 6)}:{utxo.vout}
                            </p>
                          </div>
                          {frozen && <Badge tone="loss">frozen</Badge>}
                          <button
                            type="button"
                            onClick={() =>
                              freezeUtxo.mutate({ utxoId: utxo.id, frozen: !frozen })
                            }
                            className={cx(
                              "rounded p-1 transition-colors",
                              frozen ? "text-brass" : "text-ink-faint hover:text-brass",
                            )}
                            title={frozen ? "Unfreeze this coin" : "Freeze this coin"}
                            aria-label={frozen ? "Unfreeze" : "Freeze"}
                          >
                            <Snowflake size={13} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {showCoins && (
                  <p className="border-t border-hairline px-3 py-2 text-[11px] leading-relaxed text-ink-faint">
                    Select nothing to let Gauntlet choose. Frozen coins are never
                    spent automatically — useful for keeping a tainted or
                    identity-linked output out of your other payments.
                  </p>
                )}
              </div>
            )}

            {error && <Callout tone="danger">{error}</Callout>}

            <Button
              variant="primary"
              size="lg"
              full
              icon={<SendIcon size={15} />}
              loading={build.isPending}
              disabled={
                !sendable ||
                !to ||
                !(Number(amount) > 0) ||
                addressBad ||
                !wallet?.unlocked
              }
              onClick={() => build.mutate()}
            >
              Review transaction
            </Button>

            {!wallet?.unlocked && (
              <p className="text-center text-xs text-ink-faint">
                Unlock this wallet before sending.
              </p>
            )}
          </div>
        </Panel>
      )}

      {stage === "review" && built && (
        <Panel title="Confirm">
          <div className="space-y-4">
            <div className="space-y-2.5 rounded-lg border border-hairline bg-abyss p-4 text-sm">
              <div>
                <p className="label-xs mb-1">Sending to</p>
                <p className="addr">{to}</p>
              </div>
              <hr className="border-hairline" />
              <Row
                label="Amount"
                value={`${amount} ${chain.symbol}`}
                hint={fiatText(toBaseUnits(amount, chain.decimals), chain.decimals)}
              />
              <Row
                label="Network fee"
                value={`${formatAmount(built.fee, chain.decimals)} ${chain.symbol}`}
                hint={fiatText(built.fee, chain.decimals)}
              />
              {built.changeAmount && (
                <Row
                  label="Change back to you"
                  value={`${formatAmount(built.changeAmount, chain.decimals)} ${chain.symbol}`}
                  hint={fiatText(built.changeAmount, chain.decimals)}
                />
              )}
              {built.estimatedSize && (
                <Row label="Size" value={`${built.estimatedSize} vB`} />
              )}
              <hr className="border-hairline" />
              <div className="flex justify-between font-medium">
                <span>Total leaving wallet</span>
                <span className="num text-right">
                  {formatAmount(
                    (BigInt(built.totalOut) + BigInt(built.fee)).toString(),
                    chain.decimals,
                  )}{" "}
                  {chain.symbol}
                  {fiatText(
                    (BigInt(built.totalOut) + BigInt(built.fee)).toString(),
                    chain.decimals,
                  ) && (
                    <span className="block text-[11px] font-normal text-ink-faint">
                      ≈ {fiatText((BigInt(built.totalOut) + BigInt(built.fee)).toString(), chain.decimals)}
                    </span>
                  )}
                </span>
              </div>
            </div>

            {(built.warnings ?? []).map((warning) => (
              <Callout key={warning} tone="warn">
                {warning}
              </Callout>
            ))}

            <Callout tone="danger" title="This cannot be undone">
              Once broadcast, a cryptocurrency transaction cannot be reversed,
              cancelled or refunded by anyone. Check the destination address one
              more time.
            </Callout>

            {error && <Callout tone="danger">{error}</Callout>}

            <div className="flex gap-2">
              <Button full onClick={() => setStage("compose")}>
                Back
              </Button>
              <Button
                variant="primary"
                full
                loading={send.isPending}
                onClick={() => send.mutate()}
              >
                Sign &amp; broadcast
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {stage === "sent" && (
        <Panel title="Broadcast">
          <div className="space-y-4 text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-gain/30 bg-gain/10">
              <SendIcon className="text-gain" size={20} />
            </div>
            <p className="font-medium">Transaction sent</p>
            <p className="addr rounded-lg border border-hairline bg-abyss p-3">
              {txid}
            </p>
            {chain.explorer && (
              <a
                href={chain.explorer.tx.replace("{tx}", txid)}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-block text-sm text-brass hover:underline"
              >
                View on block explorer →
              </a>
            )}
            <div className="flex items-start gap-2 text-left text-xs text-ink-faint">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warn" />
              <span>
                It may take several minutes to confirm. The balance will update
                on the next sync.
              </span>
            </div>

            {/* The moment you are most likely to want this address saved is
                right after paying it — and a saved contact is what makes the
                poisoning check useful next time. */}
            {!savedContact &&
              !contacts.some((c) => c.address === to) &&
              (saveContact !== null && (
                <div className="flex gap-2 border-t border-hairline pt-3 text-left">
                  <Input
                    placeholder="Save this address as…"
                    value={saveContact}
                    onChange={(e) => setSaveContact(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    icon={<BookUser size={14} />}
                    disabled={!saveContact.trim()}
                    loading={addContact.isPending}
                    onClick={() => addContact.mutate()}
                  >
                    Save
                  </Button>
                </div>
              ))}
            {savedContact && (
              <p className="inline-flex items-center gap-1.5 text-xs text-gain">
                <Check size={13} /> Saved to contacts.
              </p>
            )}

            <Link to="/">
              <Button variant="primary" full>
                Back to dashboard
              </Button>
            </Link>
          </div>
        </Panel>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string | null;
}) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-ink-faint">{label}</span>
      <span className="num text-right text-ink">
        {value}
        {hint && (
          <span className="block text-[11px] font-normal text-ink-faint">
            ≈ {hint}
          </span>
        )}
      </span>
    </div>
  );
}

/** Decimal string → base units, without touching floating point. */
function toBaseUnits(value: string, decimals: number): string {
  const [whole = "0", frac = ""] = value.split(".");
  const padded = frac.slice(0, decimals).padEnd(decimals, "0");
  return `${whole}${padded}`.replace(/^0+(?=\d)/, "") || "0";
}
